import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react'
import {
  ContentNodeUtils,
  DataTypeConfigUtils,
  InputValueContentNode,
} from '@helo/cms'
import { Assert, pipe } from '@helo/utils'
import { useRef, useState } from 'react'

import { ClickOutsideBoundary } from '../../components/ClickOutsideBoundary'
import { ChevronIcon } from '../../components/Icons/ChevronIcon'
import { SortEditor } from '../../components/SortEditor'
import { Tooltip } from '../../components/Tooltip/Tooltip'

export type SortEditorPopover = {
  orderByInputValueContentNode: InputValueContentNode
  onChange?: (contentNode: InputValueContentNode) => void
}

const getOrderByItems = (orderByInputValueContentNode: InputValueContentNode) =>
  pipe(
    orderByInputValueContentNode,
    ContentNodeUtils.getOfType(),
    Assert.orThrow(
      ContentNodeUtils.isKind('LIST'),
      () =>
        new Error(
          'orderByInputValueContentNode.ofType must be a ListContentNode',
        ),
    ),
    ContentNodeUtils.getItems(),
  )

const getFirstDefaultSortField = (
  orderByInputValueContentNode: InputValueContentNode,
) =>
  pipe(
    orderByInputValueContentNode,
    ContentNodeUtils.getDataTypeConfig(),
    DataTypeConfigUtils.getOfType(),
    Assert.orThrow(
      DataTypeConfigUtils.isKind('LIST'),
      () =>
        new Error(
          'orderByInputValueContentNode.ofType must be a ListDataTypeConfig',
        ),
    ),
    DataTypeConfigUtils.getOfType(),
    Assert.orThrow(
      DataTypeConfigUtils.isKind('INPUT_OBJECT'),
      () =>
        new Error(
          'orderByInputValueContentNode.ofType must be a InputObjectDataTypeConfig',
        ),
    ),
    DataTypeConfigUtils.getFields(),
    fields => fields?.find(field => field.state?.sortDisabled !== true),
  )

const getInitialOrderBy = (
  orderByInputValueContentNode: InputValueContentNode,
) => {
  const sortCount = getOrderByItems(orderByInputValueContentNode)?.length ?? 0
  const firstDefaultItem = getFirstDefaultSortField(
    orderByInputValueContentNode,
  )
  if (sortCount > 0 || !firstDefaultItem?.key) {
    return orderByInputValueContentNode
  }
  return pipe(
    orderByInputValueContentNode,
    ContentNodeUtils.updateContentNode([
      {
        [firstDefaultItem.key]: 'ASC',
      },
    ]),
  )
}

interface SortEditorPopoverContentProps extends SortEditorPopover {
  onClose: () => void
}

const SortEditorPopoverContent = ({
  orderByInputValueContentNode: orderByInputValueContentNodeProp,
  onChange,
  onClose,
}: SortEditorPopoverContentProps) => {
  const [orderByInputValueContentNode, setOrderByInputValueContentNode] =
    useState(() => getInitialOrderBy(orderByInputValueContentNodeProp))

  const handleApply = () => {
    onChange?.(orderByInputValueContentNode)
    onClose()
  }

  const handleClear = () => {
    onChange?.(
      pipe(
        orderByInputValueContentNodeProp,
        ContentNodeUtils.updateContentNode([]),
      ),
    )
    onClose()
  }

  return (
    <>
      <PopoverBody px="0">
        <SortEditor
          orderByInputValueContentNode={orderByInputValueContentNode}
          onChange={updatedOrderByInputValueContentNode => {
            setOrderByInputValueContentNode(updatedOrderByInputValueContentNode)
          }}
        />
      </PopoverBody>
      <PopoverFooter padding="16px">
        <HStack gap="8px" justifyContent="space-between" width="100%">
          <Button size="sm" variant="tertiary" onClick={handleClear}>
            Clear
          </Button>
          <HStack gap="8px">
            <Button size="sm" variant="tertiary" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={handleApply}>
              Sort
            </Button>
          </HStack>
        </HStack>
      </PopoverFooter>
    </>
  )
}

export const SortEditorPopover = ({
  orderByInputValueContentNode,
  onChange,
}: SortEditorPopover) => {
  if (!orderByInputValueContentNode) {
    throw new Error('orderByInputValueContentNodeProp is required')
  }

  const orderByItems = getOrderByItems(orderByInputValueContentNode)
  const sortCount = orderByItems?.length ?? 0
  const firstSortItem = orderByItems?.[0]
  const firstSortItemLabel = pipe(
    firstSortItem,
    Assert.orReturn(ContentNodeUtils.isKind('INPUT_OBJECT'), () => undefined),
    ContentNodeUtils.getFields(),
    fields => fields?.[0]?.state?.label as string | undefined,
  )
  const sortButtonLabelText =
    sortCount === 1 ? firstSortItemLabel : `${sortCount} columns`
  const popoverContentRef = useRef<HTMLDivElement>(null)

  return (
    <Popover placement="bottom-end" closeOnBlur={false}>
      {({ onClose, isOpen }) => (
        <>
          <PopoverTrigger>
            <Box data-sort-editor-trigger>
              <Tooltip label="Multi Column Sorting" placement="top">
                {/**  Box seems like an unnecessary element, but it's necessary for Tooltip and Popover combination.
                 * @see https://github.com/chakra-ui/chakra-ui/issues/2843
                 * */}
                <Button variant="controlButton" size="sm" gap="4px">
                  <Text fontSize="inherit">Sorted By:</Text>
                  <Text fontWeight="700" fontSize="inherit">
                    {sortButtonLabelText}
                  </Text>
                  <ChevronIcon />
                </Button>
              </Tooltip>
            </Box>
          </PopoverTrigger>
          <PopoverContent
            minWidth="439px"
            pt="8px"
            overflow="hidden"
            borderRadius="4px"
            boxShadow="0 3px 10px 0 rgba(0, 0, 0, 0.05), 0 4px 3px 0 rgba(0, 0, 0, 0.03)"
          >
            <ClickOutsideBoundary
              ref={popoverContentRef}
              boundaryId="sort-editor-popover"
              onClickOutside={onClose}
              enabled={isOpen}
            >
              {isOpen && (
                <SortEditorPopoverContent
                  orderByInputValueContentNode={orderByInputValueContentNode}
                  onChange={onChange}
                  onClose={onClose}
                />
              )}
            </ClickOutsideBoundary>
          </PopoverContent>
        </>
      )}
    </Popover>
  )
}
